import { isServiceEmail } from '../public-validators';
import { CIPXQuoteRequestIntakeFormCommunicationPreference } from '../quote/requests/intake/form/intake-form.types';
import { ActiveCommunicationContact } from './activecommunicationcontact.types';

export function boolsFromChannelValue(val: number): { email: boolean; phone: boolean; sms: boolean } {
  return { email: (val & 1) !== 0, phone: (val & 2) !== 0, sms: (val & 4) !== 0 };
}

/**
 * Channel booleans for a saved preference row, gated by the audience bitmask:
 * when the audience excludes the given bit (1 = Customer, 2 = Account User) nothing
 * is enabled regardless of any leftover channel bits.
 */
export function prefChannels(
  pref: CIPXQuoteRequestIntakeFormCommunicationPreference,
  audienceBit: number,
): { email: boolean; phone: boolean; sms: boolean } {
  if ((Number(pref.communication_setting_value ?? 0) & audienceBit) === 0) {
    return { email: false, phone: false, sms: false };
  }
  return boolsFromChannelValue(pref.communication_channel_value);
}

export interface CommunicationPrincipalContact {
  user_id: number;
  user_first_name?: string;
  user_last_name?: string;
  user_email_address?: string;
  user_phone?: string;
  user_company_label?: string;
}

export interface QuoteRequestCommunication {
  communication_preferences?: CIPXQuoteRequestIntakeFormCommunicationPreference[];
  principal_contacts?: CommunicationPrincipalContact[];
}

export interface ActiveCommunicationContactOptions {
  /** The quote request's user_id (the customer being bonded) */
  customerUserId: number;
  accountUserId?: number | null;
  accountUserEmail?: string | null;
  /** Renewal requests are driven by renewal_quote_is_ready instead of quote_is_ready */
  isRenewal: boolean;
}

/**
 * Maps the quote request `communication` blob (flat per-user x per-setting preference
 * rows plus principal contact info) to the contacts actively receiving notifications
 * about the request, sorted so contacts with usable contact info come first.
 */
export function activeCommunicationContactsFrom(
  communication: QuoteRequestCommunication | null | undefined,
  opts: ActiveCommunicationContactOptions,
): ActiveCommunicationContact[] {
  const contacts = communication?.principal_contacts ?? [];
  const prefs = (communication?.communication_preferences ?? []).filter((p) => p.communication_setting_type_code);
  const typeCode = opts.isRenewal ? 'renewal_quote_is_ready' : 'quote_is_ready';
  const accountUserEmail = String(opts.accountUserEmail ?? '').trim().toLowerCase();

  return contacts
    .map((c) => {
      const email = c.user_email_address ?? '';
      const isAccountUser =
        (opts.accountUserId != null && Number(c.user_id) === Number(opts.accountUserId)) ||
        Boolean(accountUserEmail && email.trim().toLowerCase() === accountUserEmail);
      const contactType = isAccountUser
        ? 'account_user'
        : Number(c.user_id) === Number(opts.customerUserId)
          ? 'user'
          : 'listener';

      const pref = prefs.find(
        (p) => Number(p.user_id) === Number(c.user_id) && p.communication_setting_type_code === typeCode,
      );
      const channels = pref
        ? prefChannels(pref, isAccountUser ? 2 : 1)
        : { email: false, phone: false, sms: false };

      return {
        user_id: c.user_id,
        contact_type: contactType,
        name: `${c.user_first_name ?? ''} ${c.user_last_name ?? ''}`.trim() || email || String(c.user_id),
        email,
        phone: c.user_phone,
        company: c.user_company_label,
        enabled: channels.email || channels.phone || channels.sms,
        hasContactInfo: Boolean(email && !isServiceEmail(email)) || Boolean(c.user_phone),
        channels,
      };
    })
    .filter((c) => c.enabled)
    .sort((left, right) => Number(right.hasContactInfo) - Number(left.hasContactInfo));
}
